import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import format from 'date-fns/format';
import { flatDispute, rejectValue } from 'redux/helpers';

import {
  FAKE_POST_SEND_RECEIPT,
  GET_TRANSACTIONS,
  GET_TRANSACTIONS_BY_ID,
  POST_TRANSACTION_REFUND,
  POST_TRANSACTION_CAPTURE,
  POST_TRANSACTION_ADJUST,
  POST_EXPORT_TRANSACTIONS,
  GET_REFUNDS,
  GET_PAYOUTS,
  GET_ACTIONS,
  GET_DISPUTES,
} from 'services/api';
import {
  TRANSACTION,
  TRANSACTION_ALL,
  TRANSACTION_BY_ID,
  SEND_RECEIPT,
  TRANSACTION_SEARCH,
  TRANSACTION_REFUND,
  TRANSACTION_CAPTURE,
  TRANSACTION_ADJUST,
  TRANSACTION_EXPORT,
  TRANSACTION_REFUNDS,
  TRANSACTION_PAYOUTS,
  TRANSACTION_ACTIONS,
  TRANSACTION_DISPUTES,
} from './types';
import { showSuccessMessage } from '../notification';
import {
  flatTransaction,
  flatTransactionDetail,
  flatRefund,
  flatPayout,
  flatAction,
} from 'redux/helpers';

import { DATE_FOR_API } from 'utils/dateFormat';
import { priceForAPI } from 'utils/price';
import { currentCompanySelector } from '../company';
import { capturePayment as TEXT } from 'locales/en/transaction';
import { initialRange } from 'components/DateRangePicker/ranges';
import { AMOUNT_FILTERS } from 'components/PaymentFilter/defaultFilters';

const transactionAdapter = createEntityAdapter();

export const initialState = transactionAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(initialRange.startDate, DATE_FOR_API),
    to: format(initialRange.endDate, DATE_FOR_API),
    fraud_score_max: null,
    fraud_score_min: null,
    amount_filter: AMOUNT_FILTERS[1],
    amount: null,
    statusCheckbox: true,
    status:
      'authorized,cancelled,expired,failed,partially_refunded,pending,refunded,refused,settled',
  },
  search: {
    term: '',
    filter: undefined,
    submitted: false,
  },
});

export const fakeAction = createAsyncThunk(
  SEND_RECEIPT,
  async (_, { rejectWithValue, dispatch }) => {
    try {
      await FAKE_POST_SEND_RECEIPT();
      dispatch(
        showSuccessMessage(
          'This feature is not available on API, so this is just a test message.'
        )
      );
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactions = createAsyncThunk(
  TRANSACTION_ALL,
  async (_, { rejectWithValue, getState }) => {
    try {
      const { id: companyId, ...restCompany } = currentCompanySelector(
        getState()
      );
      const { status, amount, statusCheckbox, amount_filter, ..._params } =
        getState().transaction.params;

      let params = { ..._params };
      if (statusCheckbox && status) {
        params = { ...params, status };
      }
      if (!!amount) {
        const amountForAPI = priceForAPI(amount);
        params = { ...params, [amount_filter.value]: amountForAPI };
      }
      const { data, meta } = await GET_TRANSACTIONS({
        ...params,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map((item) => {
        return flatTransaction({
          ...item,
          company: { ...restCompany, id: companyId },
        });
      });
      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchTransactions = createAsyncThunk(
  TRANSACTION_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term: searchTerm, filter: searchFilter = {} } =
        getState().transaction.search;

      if (!(searchTerm && searchFilter?.value)) {
        return;
      }

      const { data, meta } = await GET_TRANSACTIONS({
        [searchFilter.value]: searchTerm,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      const _data = data.map(flatTransaction);
      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactionById = createAsyncThunk(
  TRANSACTION_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_TRANSACTIONS_BY_ID(id);

      return flatTransactionDetail(data);
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const refund = createAsyncThunk(
  TRANSACTION_REFUND,
  async ({ id, amount, reason }, { rejectWithValue, dispatch }) => {
    try {
      await POST_TRANSACTION_REFUND({
        id,
        amount: priceForAPI(amount),
        reason,
      });

      dispatch(showSuccessMessage('Your refund request is being processed.'));

      await dispatch(getTransactionById(id));
      dispatch(getTransactionRefunds({ transaction_id: id }));
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const capture = createAsyncThunk(
  TRANSACTION_CAPTURE,
  async ({ id, amount, transactionType }, { rejectWithValue, dispatch }) => {
    try {
      await POST_TRANSACTION_CAPTURE({
        id,
        amount: priceForAPI(amount),
      });
      if (transactionType === 'adyen') {
        dispatch(showSuccessMessage(TEXT.success.captureAdyen));
      } else {
        dispatch(showSuccessMessage(TEXT.success.capture));
      }
      dispatch(getTransactionById(id));
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
export const adjustAuth = createAsyncThunk(
  TRANSACTION_ADJUST,
  async ({ id, amount }, { rejectWithValue, dispatch }) => {
    try {
      await POST_TRANSACTION_ADJUST({
        id,
        amount: priceForAPI(amount),
      });
      dispatch(showSuccessMessage(TEXT.success.adjust));
      dispatch(getTransactionById(id));
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const exportTransactions = createAsyncThunk(
  TRANSACTION_EXPORT,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const email = getState().auth.user.email;
      const { term, filter, submitted } = getState().dispute.search;

      let params = {};

      if (term && filter && submitted) {
        params = { [filter.value]: term };
      } else {
        const {
          status,
          amount,
          statusCheckbox,
          amount_filter,
          page,
          per_page,
          ..._params
        } = getState().transaction.params;

        params = { ..._params };
        if (statusCheckbox && status) {
          params = { ...params, status };
        }
        if (!!amount) {
          const amountForAPI = priceForAPI(amount);
          params = { ...params, [amount_filter.value]: amountForAPI };
        }
      }

      await POST_EXPORT_TRANSACTIONS(params);
      dispatch(
        showSuccessMessage(
          `Check your email account ${email} for access to the file.`
        )
      );
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactionRefunds = createAsyncThunk(
  TRANSACTION_REFUNDS,
  async ({ transaction_id, page = 1 }, { rejectWithValue, getState }) => {
    try {
      const { data, meta } = await GET_REFUNDS({
        transaction_id,
        page,
        per_page: 10,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatRefund);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactionDisputes = createAsyncThunk(
  TRANSACTION_DISPUTES,
  async ({ transaction_id, page = 1 }, { rejectWithValue, getState }) => {
    try {
      const { data, meta } = await GET_DISPUTES({
        transaction_id,
        page,
        per_page: 10,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatDispute);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactionPayouts = createAsyncThunk(
  TRANSACTION_PAYOUTS,
  async ({ transaction_id, page = 1 }, { rejectWithValue, getState }) => {
    try {
      const { data, meta } = await GET_PAYOUTS({
        transaction_id,
        page,
        per_page: 10,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatPayout);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getTransactionActions = createAsyncThunk(
  TRANSACTION_ACTIONS,
  async ({ transaction_id, page = 1 }, { rejectWithValue }) => {
    try {
      const { data, meta } = await GET_ACTIONS({
        transaction_id,
        page,
        per_page: 10,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };
      const _data = data.map(flatAction);

      return { data: _data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

const transactionSlice = createSlice({
  name: TRANSACTION,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchFilter: (state, { payload }) => {
      state.search.filter = payload;
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fakeAction.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fakeAction.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(fakeAction.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(exportTransactions.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(exportTransactions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(exportTransactions.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(getTransactions.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getTransactions.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getTransactions.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        transactionAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getTransactionRefunds.fulfilled, (state, action) => {
        transactionAdapter.updateOne(state, {
          id: action.meta.arg.transaction_id,
          changes: {
            refunds: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addCase(getTransactionPayouts.fulfilled, (state, action) => {
        transactionAdapter.updateOne(state, {
          id: action.meta.arg.transaction_id,
          changes: {
            payouts: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addCase(getTransactionActions.fulfilled, (state, action) => {
        transactionAdapter.updateOne(state, {
          id: action.meta.arg.transaction_id,
          changes: {
            actions: {
              data: action.payload.data,
              pagination: {
                ...action.payload.pagination,
                hasStaticPerPage: true,
              },
            },
          },
        });
      })
      .addCase(searchTransactions.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchTransactions.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchTransactions.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        transactionAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getTransactionById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getTransactionById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getTransactionById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        transactionAdapter.upsertOne(state, payload);
      })
      .addDefaultCase((state) => state);
  },
});

export const transactionSelectors = transactionAdapter.getSelectors(
  (state) => state.transaction
);

export const transactionByIdSelector = (state, id) => ({
  data: transactionSelectors.selectById(state, id),
  isLoading: state.transaction.detailsIsLoading,
});
export const transactionAllSelector = (state) => ({
  data: transactionSelectors.selectAll(state),
  pagination: state.transaction.pagination,
  isLoading: state.transaction.isLoading,
  params: state.transaction.params,
  searchTerm: state.transaction.search.term,
  searchFilter: state.transaction.search.filter,
});
export const { setParams, setSearchFilter, setSearchTerm, resetSearch } =
  transactionSlice.actions;

export default transactionSlice.reducer;
